import * as THREE from 'three/webgpu';
import {
  uniform,
  time,
  uv,
  float,
  vec2,
  sin,
  length,
  min,
  smoothstep,
} from 'three/tsl';
import { setEmissiveNode, hexGrid, forceFieldPosition } from './tslUtils';
import { ARENA_SIZE_Y } from '../../shared/constants';

/**
 * Danger-zone amber — chosen to contrast with the cyan outer force-field so
 * the player can distinguish the shrinking safe boundary from the static arena.
 */
const ZONE_COLOR = 0xffa94d;
const DEFAULT_RADIUS = 100;

/** Seconds to smooth displayed radius ≈ to the server target. Matches the
 *  server's `BR_SHRINK_TRANSITION_SEC`. */
const SHRINK_SMOOTH_SEC = 30;

interface ZoneState {
  mode?: string;
  phase?: string;
  zoneRadius?: number;
  zoneCenterX?: number;
  zoneCenterZ?: number;
}

/**
 * Renders the Battle Royale safe-zone: a hollow translucent cylinder that
 * shrinks over time. Visible only when `state.mode === 'br'` and the match is
 * in the `playing` or `finished` phase.
 *
 * Geometry is built with unit radius (r = 1) and animated via scale so the
 * mesh never needs to be rebuilt.
 */
export class ZoneRenderer {
  private readonly mesh: THREE.Mesh;
  private readonly geometry: THREE.CylinderGeometry;
  private readonly material: THREE.MeshBasicNodeMaterial;
  private readonly scene: THREE.Scene;
  private displayedRadius = DEFAULT_RADIUS;

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    this.geometry = new THREE.CylinderGeometry(
      1,
      1,
      ARENA_SIZE_Y,
      64,
      1,
      /* openEnded */ true,
    );

    this.material = this.buildMaterial();

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.position.set(0, ARENA_SIZE_Y / 2, 0);
    this.mesh.scale.set(this.displayedRadius, 1, this.displayedRadius);
    this.mesh.visible = false;
    // Render last; translucent no-depth-write cylinders should draw over terrain.
    this.mesh.renderOrder = 2;

    scene.add(this.mesh);
  }

  update(deltaMs: number, state: ZoneState | null | undefined): void {
    if (!state || state.mode !== 'br' || state.phase === 'waiting') {
      this.mesh.visible = false;
      return;
    }

    this.mesh.visible = true;

    const centerX = state.zoneCenterX ?? 0;
    const centerZ = state.zoneCenterZ ?? 0;
    if (centerX !== this.mesh.position.x || centerZ !== this.mesh.position.z) {
      this.mesh.position.set(centerX, ARENA_SIZE_Y / 2, centerZ);
    }

    // Frame-rate-independent exponential smoothing toward the server target.
    const targetRadius = state.zoneRadius ?? DEFAULT_RADIUS;
    const delta = targetRadius - this.displayedRadius;
    if (Math.abs(delta) > 0.01) {
      const alpha = 1 - Math.exp(-deltaMs / (SHRINK_SMOOTH_SEC * 1000) * 4);
      this.displayedRadius += delta * alpha;
      this.mesh.scale.x = this.displayedRadius;
      this.mesh.scale.z = this.displayedRadius;
    } else if (this.displayedRadius !== targetRadius) {
      this.displayedRadius = targetRadius;
      this.mesh.scale.x = targetRadius;
      this.mesh.scale.z = targetRadius;
    }
  }

  dispose(): void {
    this.scene.remove(this.mesh);
    this.geometry.dispose();
    this.material.dispose();
  }

  private buildMaterial(): THREE.MeshBasicNodeMaterial {
    const mat = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      side: THREE.DoubleSide,
      depthWrite: false,
      blending: THREE.NormalBlending,
    });

    mat.positionNode = forceFieldPosition();

    const fieldColor = uniform(new THREE.Color(ZONE_COLOR));
    const baseOpacity = uniform(0.12);

    // `uv()` must be read inside the material graph.
    const uvNode = uv().toVar();
    const hex = hexGrid(uvNode, float(18.0));
    const ripple = sin(
      length(uvNode.sub(vec2(0.5, 0.5))).mul(28.0).sub(time.mul(3.3)),
    ).mul(0.5).add(0.5);
    const scan = sin(uvNode.y.mul(120.0).add(time.mul(4.5))).mul(0.5).add(0.5);
    const edgeDist = min(uvNode.y, uvNode.y.oneMinus());
    const edgeGlow = smoothstep(float(0.0), float(0.18), edgeDist).oneMinus();
    const intensity = hex.mul(0.55)
      .add(edgeGlow.mul(0.55))
      .add(ripple.mul(0.22))
      .add(scan.mul(0.1));

    const fragColor = fieldColor.mul(float(0.6).add(intensity));
    const fragAlpha = baseOpacity.add(intensity.mul(0.2));

    mat.colorNode = fragColor;
    setEmissiveNode(mat, fragColor);
    mat.opacityNode = fragAlpha;

    return mat;
  }
}
